import React, { useState, useRef, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import {
  resolveTripPaidBinary,
  resolveTripPaymentStatus,
  TRIP_PAYMENT_STATE,
} from '../../../utils/tripPaymentStatus';
import {
  resolveTripPaymentMode,
  hasServerPaymentBlock,
  TRIP_PAYMENT_MODE,
} from '../../../utils/tripPaymentMode';

// Payment mode (cash/card/account) comes from src/utils/tripPaymentMode.js. It used to
// be duplicated here with the note "promote if a second caller appears" — a second
// caller did appear, and the copies then had to be changed in lockstep. Importing the
// shared one means the settled-method rule lives in exactly one place.

const PickupArrivalModal = ({ 
  isOpen, 
  onClose, 
  waypoint, 
  onLoad, 
  onNoShow,
  onSignature,
  onCopayConfirm,
  onCancel 
}) => {
  // 'idle' | 'saving' | 'saved' | 'error'. Only 'saved' means the server confirmed the
  // PNG reached Reservation_File — that is what gates Pickup. A plain boolean here used to
  // flip true on tap, letting a trip be loaded when the write had failed.
  const [signatureStatus, setSignatureStatus] = useState('idle');
  const [signatureError, setSignatureError] = useState('');
  const [copayConfirmed, setCopayConfirmed] = useState(false);
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  // Last captured data URL, so Retry re-sends the same strokes without asking the
  // passenger to sign again.
  const capturedSignatureRef = useRef(null);

  // Get trip details from waypoint
  const trip = waypoint?.trip || {};
  const passenger = trip.passenger || {};
  const pickup = trip.pickup || {};
  const dropoff = trip.dropoff || {};
  const reservation = trip.reservation || {};

  // Check if signature or copay is required (from priceItems)
  const priceItems = reservation.priceItems || [];
  const signatureRequired = priceItems.some(item => item.Name === 'Signature') || reservation.signature === true;
  const copayItem = priceItems.find(item => item.Name === 'Copay');
  const copayRequired = copayItem || (!!reservation.copay && reservation.copay > 0);
  const copayAmount = copayItem?.TripAmount || reservation.copay || '';

  // ACET hides the account detail line below the Payment chip. Same fleet-code
  // resolution the other ACET gates use.
  const isAcetFleet = (localStorage.getItem('fleetCode') || '').toUpperCase() === 'ACET';

  // One payment status only: Account Trip when billed to an account (the
  // Account_ContactId is not the fleet's CASH/CC contact), otherwise Paid/Unpaid from
  // Trip_Payment.PaymentStatus = 'Settled'. Mirrors the server's own precedence in
  // paymentAPI.js summarizeTrip() — accountBilled is checked before paid/unpaid.
  const hasPaymentData = hasServerPaymentBlock(reservation);
  const paymentMode = resolveTripPaymentMode(trip);
  const isAccountTrip = hasPaymentData
    ? resolveTripPaymentStatus(reservation).state === TRIP_PAYMENT_STATE.ACCOUNT_BILLED
    : paymentMode.isAccountBilled;
  const paidStatus = resolveTripPaidBinary(reservation);

  // Fare $: on an account-billed trip (Account_Contact is not the fleet's CASH/CC
  // contact) show Trip.DriverGets — what the DRIVER earns, since the rider pays nothing
  // at the door. Otherwise show what the passenger hands over: `passengerPays` when the
  // server sent it (it is copay-aware — an active Copay line wins over TripCost), else
  // Trip.TripCost. Both driver-money fields come from lib/driverTripMoney server-side.
  // Always rendered, defaulting to 0.00 rather than hiding the chip.
  const driverGetsRaw = reservation.driverGets !== undefined
    ? reservation.driverGets
    : reservation.DriverGets;
  const passengerPaysRaw = reservation.passengerPays !== undefined
    ? reservation.passengerPays
    : reservation.PassengerPays;
  const riderAmountRaw = passengerPaysRaw !== undefined && passengerPaysRaw !== null
    ? passengerPaysRaw
    : reservation.price;
  const fareAmount = Number(isAccountTrip ? driverGetsRaw : riderAmountRaw) || 0;
  const fare = { text: fareAmount.toFixed(2) };

  // Get passenger attributes for display
  const passengerAttributes = passenger.attributes || [];

  // Helper function to format address
  const formatAddress = (location) => {
    if (!location) return 'N/A';
    
    // If landmark exists, show "Landmark Name, State"
    if (location.landmark) {
      return `${location.landmark}, ${location.state || ''}`.trim();
    }
    
    // Otherwise construct from streetNumber, streetName, city
    if (location.streetNumber && location.streetName) {
      return `${location.streetNumber} ${location.streetName}, ${location.city || ''}`.trim();
    }
    
    // Fallback to full address
    return location.address || 'N/A';
  };

  // Determine if Load button should be enabled
  const canLoad = (!signatureRequired || signatureStatus === 'saved') && (!copayRequired || copayConfirmed);

  // Initialize signature pad when modal opens
  useEffect(() => {
    if (isOpen && canvasRef.current && signatureRequired) {
      const canvas = canvasRef.current;
      
      // Set canvas size based on container
      const resizeCanvas = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const container = canvas.parentElement;
        canvas.width = container.offsetWidth * ratio;
        canvas.height = 120 * ratio;
        canvas.style.width = `${container.offsetWidth}px`;
        canvas.style.height = '120px';
        canvas.getContext('2d').scale(ratio, ratio);
        
        if (signaturePadRef.current) {
          signaturePadRef.current.clear();
        }
      };

      signaturePadRef.current = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)'
      });

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      return () => {
        window.removeEventListener('resize', resizeCanvas);
        if (signaturePadRef.current) {
          signaturePadRef.current.off();
        }
      };
    }
  }, [isOpen, signatureRequired]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSignatureStatus('idle');
      setSignatureError('');
      capturedSignatureRef.current = null;
      setCopayConfirmed(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
    capturedSignatureRef.current = null;
    setSignatureStatus('idle');
    setSignatureError('');
  };

  // Sends the captured PNG and waits for the server to confirm it was stored. Pickup stays
  // locked until that ack lands, so a failed write can no longer be mistaken for a save.
  const saveSignature = async (dataUrl) => {
    capturedSignatureRef.current = dataUrl;
    setSignatureStatus('saving');
    setSignatureError('');

    if (!onSignature) {
      setSignatureStatus('error');
      setSignatureError('Signature cannot be saved right now. Please try again.');
      return;
    }

    try {
      await onSignature(dataUrl);
      setSignatureStatus('saved');
      setSignatureError('');
    } catch (err) {
      setSignatureStatus('error');
      setSignatureError((err && err.message) || 'Could not save the signature. Please try again.');
    }
  };

  const handleConfirmSignature = () => {
    const pad = signaturePadRef.current;

    // Previously this branch fell through silently, so tapping Confirm on an empty pad
    // did nothing at all with no explanation.
    if (!pad || pad.isEmpty()) {
      setSignatureStatus('error');
      setSignatureError('Please capture a signature before confirming.');
      return;
    }

    saveSignature(pad.toDataURL('image/png'));
  };

  // Re-sends the strokes already captured; the passenger does not sign again.
  const handleRetrySignature = () => {
    const dataUrl = capturedSignatureRef.current;
    if (!dataUrl) {
      handleConfirmSignature();
      return;
    }
    saveSignature(dataUrl);
  };

  const handleConfirmCopay = () => {
    setCopayConfirmed(true);
    
    // Send copay confirmation to server
    if (onCopayConfirm) {
      onCopayConfirm(copayAmount);
    }
  };

  const handleLoad = () => {
    if (canLoad) {
      onLoad();
      onClose();
    }
  };

  const handleNoShow = () => {
    onNoShow();
    onClose();
  };

  // Determine header color based on seating capacity type
  const isWheelchair = reservation.wheelchair > 0 || waypoint?.wheelChair;
  const headerColor = isWheelchair ? "bg-blue-500" : "bg-orange-500";

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2200]"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          // Don't close on backdrop click - user must complete action
        }
      }}
    >
      <div className="bg-white rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className={`${headerColor} text-white px-4 py-3 rounded-t-lg flex justify-between items-center`}>
          <h2 className="text-lg font-bold">Pickup</h2>
          <button
            onClick={() => {
              if (onCancel) onCancel();
              onClose();
            }}
            className="text-white hover:text-gray-200 p-1"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-2 space-y-2">
          {/* Passenger Details with Attributes and Trip Items */}
          <div className="border-b pb-1">
            <h3 className="font-semibold text-gray-700 mb-1">Passenger</h3>
            <div className="flex gap-2">
              {/* Left: Name and Account */}
              <div className="flex-1">
                <p className="text-lg font-bold">{passenger.name || waypoint?.passengerName || 'Unknown'}</p>
                {(hasPaymentData || paymentMode.mode !== TRIP_PAYMENT_MODE.UNKNOWN) && (
                  <p className="text-sm text-gray-600 mt-0.5">
                    <span className="text-gray-500">Payment: </span>
                    <span
                      className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold ${
                        isAccountTrip
                          ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
                          : paidStatus.isPaid
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                          : 'bg-orange-50 text-orange-700 border-orange-300'
                      }`}
                    >
                      {isAccountTrip ? 'Account Trip' : paidStatus.label}
                    </span>
                  </p>
                )}
                {!isAcetFleet && isAccountTrip && paymentMode.detail && (
                  <p className="text-xs text-gray-500 mt-0.5">{paymentMode.detail}</p>
                )}
              </div>
              
              {/* Right: Attributes and Trip Items */}
              <div className="flex-1 space-y-1">
                {/* Passenger Attributes */}
                {passengerAttributes.length > 0 && passengerAttributes[0] !== 0 && (
                  <div className="flex flex-wrap gap-1">
                    {passengerAttributes.map((attr, index) => (
                      <span 
                        key={index}
                        className="inline-block bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-xs"
                      >
                        {attr}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Fare: DriverGets for an Account Trip, TripCost otherwise — see the
                    isAccountTrip branch above. Always shown, defaulting to $0.00 when
                    the relevant field hasn't been sent yet. */}
                <div className="flex flex-wrap gap-1">
                  <span className="inline-block bg-amber-100 text-amber-900 px-2 py-0.5 rounded text-xs font-semibold">
                    Fare: ${fare.text}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Pickup Address */}
          <div className="border-b pb-1">
            <h3 className="font-semibold text-gray-700 mb-1">Pickup</h3>
            <p className="text-sm">{formatAddress(pickup)}</p>
            {pickup.comments && (
              <p className="text-sm text-gray-600 mt-1">
                {pickup.comments}
              </p>
            )}
          </div>

          {/* Dropoff Address */}
          <div className="border-b pb-1">
            <h3 className="font-semibold text-gray-700 mb-1">Dropoff</h3>
            <p className="text-sm">{formatAddress(dropoff)}</p>
            {dropoff.comments && (
              <p className="text-sm text-gray-600 mt-1">
                {dropoff.comments}
              </p>
            )}
          </div>

          {/* Wheelchair Indicator */}
          {(reservation.wheelchair > 0 || waypoint?.wheelChair) && (
            <div className="border-b pb-1">
              <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                ♿ Wheelchair
              </span>
            </div>
          )}

          {/* Signature Section (Optional) */}
          {signatureRequired && (
            <div className="border-b pb-1">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-700">
                  Signature <span className="text-red-500">*</span>
                </h3>
                {signatureStatus === 'saving' && (
                  <span className="text-blue-600 text-sm font-medium">Saving…</span>
                )}
                {signatureStatus === 'saved' && (
                  <span className="text-green-600 text-sm font-medium">✓ Confirmed</span>
                )}
              </div>

              {signatureStatus !== 'saved' ? (
                <>
                  {/* Canvas stays mounted while saving and after a failure so the captured
                      strokes survive a Retry (and the pad never binds to a detached node). */}
                  <div className="border border-gray-300 rounded bg-white">
                    <canvas
                      ref={canvasRef}
                      className="w-full touch-none"
                      style={{ height: '120px' }}
                    />
                  </div>
                  {signatureStatus === 'error' && signatureError && (
                    <p className="text-xs text-red-600 mt-1">{signatureError}</p>
                  )}
                  <div className="flex space-x-2 mt-2">
                    <button
                      onClick={handleClearSignature}
                      disabled={signatureStatus === 'saving'}
                      className={`flex-1 py-1 px-3 rounded text-sm ${
                        signatureStatus === 'saving'
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Clear
                    </button>
                    <button
                      onClick={signatureStatus === 'error' ? handleRetrySignature : handleConfirmSignature}
                      disabled={signatureStatus === 'saving'}
                      className={`flex-1 py-1 px-3 text-white rounded text-sm ${
                        signatureStatus === 'saving'
                          ? 'bg-blue-300 cursor-not-allowed'
                          : 'bg-blue-500 hover:bg-blue-600'
                      }`}
                    >
                      {signatureStatus === 'saving'
                        ? 'Saving…'
                        : signatureStatus === 'error' ? 'Retry' : 'Confirm Signature'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="border border-green-300 rounded bg-green-50 p-2 text-center">
                  <span className="text-green-700">Signature captured</span>
                </div>
              )}
            </div>
          )}

          {/* Co-pay Section (Optional) */}
          {copayRequired && copayAmount > 0 && (
            <div className="border-b pb-1">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-700">
                  Co-pay Required <span className="text-red-500">*</span>
                </h3>
                {copayConfirmed && (
                  <span className="text-green-600 text-sm font-medium">✓ Confirmed</span>
                )}
              </div>
              
              <div className={`p-3 rounded ${copayConfirmed ? 'bg-green-50 border border-green-300' : 'bg-orange-50 border border-orange-300'}`}>
                <p className="text-lg font-bold text-center">
                  ${copayAmount.toFixed(2)}
                </p>
                {!copayConfirmed && (
                  <button
                    onClick={handleConfirmCopay}
                    className="w-full mt-2 py-1 px-3 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                  >
                    Confirm Co-pay Collected
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-2 pt-1">
            <button
              onClick={handleNoShow}
              className="flex-1 py-3 px-4 bg-red-500 text-white font-bold rounded hover:bg-red-600"
            >
              No Show
            </button>
            <button
              onClick={handleLoad}
              disabled={!canLoad}
              className={`flex-1 py-3 px-4 font-bold rounded ${
                canLoad 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Pickup
            </button>
          </div>

          {/* Helper text if requirements not met */}
          {!canLoad && (
            <p className="text-xs text-center text-red-500">
              {signatureRequired && signatureStatus !== 'saved' && (
                signatureStatus === 'saving' ? 'Saving signature… ' : 'Signature required. '
              )}
              {!copayConfirmed && copayRequired && 'Co-pay confirmation required.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickupArrivalModal;
